import React from "react";
import { Text, View } from "native-base";
import FastImage from "@d11/react-native-fast-image";
import { Minus, Plus, Trash2 } from "lucide-react-native";
import { Platform } from "react-native";
import type { ResolvedCartItem } from "../Universal/API/Context-API/CartContext";
import { webFont } from "../Universal/API/API";
import PressableWrapp from "../Universal/Buttons/PressableReact/PressableWrapp";
import {
  formatCurrency,
  getCartItemImage,
  getResolvedUnitPrice,
  getSavedOrderItemUnitPrice,
} from "./helpers";
import type { SavedOrderItem } from "./cartTypes";

type CartItemCardProps = {
  item: ResolvedCartItem;
  scaleAll: number;
  isSmallVersion: boolean;
  showDiscountPrices: boolean;
  onPressItem?: (id: string) => void;
  onIncrement?: (id: string | number) => void;
  onDecrement?: (id: string | number) => void;
  onRemove?: (id: string | number) => void;
  compact?: boolean;
  withTopBorder?: boolean;
  readonly?: boolean;
  savedItem?: SavedOrderItem | null;
};

const webCursorPointer = Platform.OS === "web" ? ({ cursor: "pointer" } as any) : null;

const CartItemCard: React.FC<CartItemCardProps> = ({
  item,
  scaleAll,
  isSmallVersion,
  showDiscountPrices,
  onPressItem,
  onIncrement,
  onDecrement,
  onRemove,
  compact = false,
  withTopBorder = false,
  readonly = false,
  savedItem = null,
}) => {
  const imageUri = getCartItemImage(item.unit);
  const unitPrice = savedItem
    ? getSavedOrderItemUnitPrice({
        item: savedItem,
        showDiscountPrices,
      })
    : getResolvedUnitPrice({
        unit: item.unit,
        showDiscountPrices,
      });
  const itemTotal = unitPrice * item.quantity;

  const imageSize = compact ? 76 * scaleAll : isSmallVersion ? 88 * scaleAll : 104 * scaleAll;
  const titleSize = compact ? 14 * scaleAll : 16 * scaleAll;
  const titleLineHeight = compact ? 18 * scaleAll : 20 * scaleAll;

  return (
    <View
      style={{
        paddingVertical: compact ? 14 * scaleAll : 16 * scaleAll,
        borderTopWidth: withTopBorder ? 1 : 0,
        borderTopColor: "#EFEDE8",
        flexDirection: "row",
      }}
    >
      <PressableWrapp
        onPress={onPressItem ? () => onPressItem(item.id) : undefined}
        style={[
          {
            width: imageSize,
            height: imageSize,
            borderRadius: compact ? 18 * scaleAll : 22 * scaleAll,
            backgroundColor: "#FAFAF8",
            borderWidth: 1,
            borderColor: "#EFEDE8",
            marginRight: 12 * scaleAll,
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
          },
          onPressItem ? webCursorPointer : null,
        ]}
      >
        {imageUri ? (
          <FastImage
            source={{ uri: imageUri }}
            style={{ width: "100%", height: "100%" }}
            resizeMode="contain"
          />
        ) : null}
      </PressableWrapp>

      <View style={{ flex: 1, minWidth: 0 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            marginBottom: 8 * scaleAll,
          }}
        >
          <PressableWrapp
            onPress={onPressItem ? () => onPressItem(item.id) : undefined}
            style={[{ flex: 1, paddingRight: 8 * scaleAll }, onPressItem ? webCursorPointer : null]}
          >
            <Text
              selectable={false}
              numberOfLines={2}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: titleSize,
                color: "#111111",
                fontWeight: "700",
                lineHeight: titleLineHeight,
              }}
            >
              {item.unit.name}
            </Text>
            <Text
              selectable={false}
              numberOfLines={1}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 12 * scaleAll,
                color: "#7A746C",
                fontWeight: "600",
                marginTop: 4 * scaleAll,
              }}
            >
              {item.unit.art || item.unit.id}
            </Text>
          </PressableWrapp>

          {!readonly && onRemove ? (
            <PressableWrapp
              onPress={() => onRemove(item.id)}
              style={[
                {
                  width: 34 * scaleAll,
                  height: 34 * scaleAll,
                  borderRadius: 12 * scaleAll,
                  backgroundColor: "#F5F4F1",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                },
                webCursorPointer,
              ]}
            >
              <Trash2 size={15 * scaleAll} color="#5F5B55" />
            </PressableWrapp>
          ) : null}
        </View>

        <View
          style={{
            flexDirection: isSmallVersion ? "column" : "row",
            alignItems: isSmallVersion ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: 10 * scaleAll,
          }}
        >
          <View>
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 11 * scaleAll,
                color: "#7A746C",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                marginBottom: 3 * scaleAll,
              }}
            >
              Сумма
            </Text>
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: compact ? 18 * scaleAll : 20 * scaleAll,
                color: "#111111",
                fontWeight: "700",
              }}
            >
              {formatCurrency(itemTotal)} ₽
            </Text>
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 12 * scaleAll,
                color: "#7A746C",
                fontWeight: "600",
                marginTop: 2 * scaleAll,
              }}
            >
              {formatCurrency(unitPrice)} ₽ × {item.quantity} {item.unit.ed || "шт."}
            </Text>
          </View>

          {readonly ? (
            <View
              style={{
                minWidth: compact ? 152 * scaleAll : 164 * scaleAll,
                borderRadius: 18 * scaleAll,
                backgroundColor: "#FBFAF8",
                borderWidth: 1,
                borderColor: "#ECE8E1",
                paddingHorizontal: 14 * scaleAll,
                paddingVertical: 12 * scaleAll,
              }}
            >
              <Text
                selectable={false}
                style={{
                  fontFamily: webFont("Inter"),
                  fontSize: 11 * scaleAll,
                  color: "#7A746C",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  marginBottom: 4 * scaleAll,
                }}
              >
                Количество
              </Text>
              <Text
                selectable={false}
                style={{
                  fontFamily: webFont("Inter"),
                  fontSize: 18 * scaleAll,
                  color: "#111111",
                  fontWeight: "700",
                }}
              >
                {item.quantity} {item.unit.ed || "шт."}
              </Text>
            </View>
          ) : (
            <View
              style={{
                minWidth: compact ? 152 * scaleAll : 164 * scaleAll,
                borderRadius: 18 * scaleAll,
                backgroundColor: "#FBFAF8",
                borderWidth: 1,
                borderColor: "#ECE8E1",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                padding: 6 * scaleAll,
              }}
            >
              <PressableWrapp
                onPress={() => onDecrement?.(item.id)}
                style={[
                  {
                    width: 38 * scaleAll,
                    height: 38 * scaleAll,
                    borderRadius: 14 * scaleAll,
                    backgroundColor: "#FFFFFF",
                    borderWidth: 1,
                    borderColor: "#E5E2DB",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  webCursorPointer,
                ]}
              >
                <Minus size={16 * scaleAll} color="#111111" />
              </PressableWrapp>

              <Text
                selectable={false}
                style={{
                  fontFamily: webFont("Inter"),
                  fontSize: 18 * scaleAll,
                  color: "#111111",
                  fontWeight: "700",
                }}
              >
                {item.quantity}
              </Text>

              <PressableWrapp
                onPress={() => onIncrement?.(item.id)}
                style={[
                  {
                    width: 38 * scaleAll,
                    height: 38 * scaleAll,
                    borderRadius: 14 * scaleAll,
                    backgroundColor: "#111111",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  webCursorPointer,
                ]}
              >
                <Plus size={16 * scaleAll} color="#FFFFFF" />
              </PressableWrapp>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export default React.memo(CartItemCard);
